var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// x-plain-dialog.ts
import { AdornisRouter } from "@adornis/base/client/a-router";
import { html } from "lit";
import { customElement } from "lit/decorators.js";
import { animationFrameScheduler, BehaviorSubject, firstValueFrom, fromEvent, Observable, Subject, timer } from "rxjs";
import { skip, take, takeUntil, throttleTime } from "rxjs/operators";
import { ChemistryLitElement } from "@adornis/chemistry/chemistry-lit-element";
import { intrinsicHeight } from "@adornis/chemistry/controllers/loader";
import { Pan, Region, Track } from "westures";
var OpenState = /* @__PURE__ */ ((OpenState2) => {
  OpenState2[OpenState2["OPEN"] = 0] = "OPEN";
  OpenState2[OpenState2["HALF"] = 1] = "HALF";
  OpenState2[OpenState2["CLOSED"] = 2] = "CLOSED";
  return OpenState2;
})(OpenState || {});
var defaultOptions = {
  targetParent: document.body,
  props: {},
  steps: [
    { height: 0, dialogInteraction: false, backgroundInteraction: true },
    { height: 25, dialogInteraction: false, backgroundInteraction: true },
    { height: 75, dialogInteraction: true, backgroundInteraction: false }
  ],
  startStep: 2,
  modal: false,
  draggable: false,
  enableSteps: false,
  closeOnBottomStep: true,
  closeOnNavigate: true,
  backgroundOpacity: 0.6,
  maxHeightFactor: 0.8,
  closeOnEscape: false
};
var animationFrame = () => new Promise((resolve) => requestAnimationFrame(() => resolve()));
var ElementsPlainDialog = class extends ChemistryLitElement {
  constructor() {
    super(...arguments);
    this.result = new Promise((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
    });
  }
  static get element_name() {
    if (this === ElementsPlainDialog)
      return "x-plain-dialog";
    throw new Error("You need to define the element_name getter in order to make x-dialog work properly: " + this.name);
  }
  initDialog(options = defaultOptions) {
    this.options = { ...defaultOptions, ...options, draggable: options.draggable || options.enableSteps || false };
    Object.entries(this.options.props).forEach(([key, value]) => {
      const self = this;
      if (self[key] instanceof Observable)
        self[key].next(value);
      else
        self[key] = value;
    });
    this.dialogElements = {
      backdrop: this.getBackdrop(this.options),
      outerWrapper: this.getOuterWrapper(this.options),
      innerWrapper: this.getInnerWrapper(this.options),
      contentWrapper: this.getContentWrapper(this.options)
    };
    if (!this.options.modal)
      fromEvent(this.dialogElements.backdrop, "click").pipe(take(1)).subscribe(() => this.cancel());
    if (this.options.draggable) {
      this.dialogElements.dragWrapper = this.getDragWrapper(this.options);
      this.dialogElements.dragElement = this.getDragElement(this.options);
      this.dragHandler = new DialogDragHandler(this);
    }
  }
  connectedCallback() {
    if (!this.dialogElements || !this.options)
      throw new Error(`connecting uninitialized dialog! ${this.nodeName}`);
    super.connectedCallback();
    if (this.options.closeOnNavigate)
      AdornisRouter.get().path.pipe(skip(1), takeUntil(this.disconnected)).subscribe(() => this.cancel());
    if (this.options.closeOnEscape) {
      fromEvent(document, "keydown").pipe(takeUntil(this.disconnected)).subscribe((e) => {
        const event = e;
        if (event.key === "Escape")
          this.cancel();
      });
    }
    animationFrame().then((_) => animationFrame()).then((_) => this.transitionIn());
    if (this.options && !this.options.draggable) {
      intrinsicHeight.pipe(skip(1), takeUntil(this.disconnected)).subscribe((height) => {
        if (!this.dialogElements)
          return;
        this.dialogElements.outerWrapper.style.height = `${height}px`;
        this.dialogElements.innerWrapper.style.maxHeight = `${this.options.maxHeightFactor * height}px`;
        this.dialogElements.contentWrapper.style.maxHeight = `${this.options.maxHeightFactor * height}px`;
      });
    }
  }
  render() {
    return this.dialogShowPopupContent || html`no content set`;
  }
  async transitionIn() {
    if (!this.dialogElements || !this.options)
      throw new Error(`operation on uninitialized dialog! ${this.nodeName}`);
    if (this.options.draggable) {
      const translateDist = this.dragHandler.getTranslateDistance(this.options.startStep);
      const startStep = this.options.steps[this.options.startStep];
      if (startStep?.backgroundInteraction) {
        this.dialogElements.backdrop.style.opacity = "0";
        this.dialogElements.outerWrapper.style.transform = `translateY(${translateDist}px)`;
        this.dialogElements.innerWrapper.style.transform = this.options.steps[this.options.startStep].height >= 95 ? "translateY(env(safe-area-inset-top))" : "translateY(0px)";
      } else {
        this.dialogElements.backdrop.style.opacity = this.options.backgroundOpacity + "";
        this.dialogElements.innerWrapper.style.transform = startStep.height >= 95 ? `translateY(calc(${translateDist}px + env(safe-area-inset-top)))` : `translateY(${translateDist}px)`;
      }
    } else {
      this.dialogElements.innerWrapper.style.transform = "scale(1)";
      this.dialogElements.backdrop.style.opacity = this.options.backgroundOpacity + "";
    }
    this.dialogElements.innerWrapper.style.opacity = "1";
    await firstValueFrom(timer(300));
  }
  async transitionOut() {
    if (!this.dialogElements || !this.options)
      throw new Error(`operation on uninitialized dialog! ${this.nodeName}`);
    this.dialogElements.backdrop.style.opacity = "0";
    this.dialogElements.innerWrapper.style.transform = this.options.draggable ? "translateY(100%)" : "scale(.8)";
    this.dialogElements.innerWrapper.style.opacity = "0";
    await firstValueFrom(timer(300));
  }
  async close(returnVal) {
    await this.transitionOut();
    this.resolve(returnVal);
  }
  async cancel(returnVal) {
    await this.transitionOut();
    this.reject(returnVal);
  }
  getBackdrop({ targetParent }) {
    const backdropElem = document.createElement("div");
    backdropElem.classList.add("backdrop");
    backdropElem.setAttribute(
      "style",
      `position: ${targetParent === document.body ? "fixed" : "absolute"}; top: 0; left: 0; height: 100%; width: ${targetParent === document.body ? "100vw" : "100%"}; background: #000000; opacity: 0; z-index: -1; transition: opacity .3s ease`
    );
    return backdropElem;
  }
  getOuterWrapper({ targetParent, draggable, startStep }) {
    const wrapperElem = document.createElement("div");
    wrapperElem.classList.add("outer-wrapper");
    wrapperElem.setAttribute(
      "style",
      `position: ${targetParent === document.body ? "fixed" : "absolute"}; top: 0; left: 0; height: ${targetParent === document.body ? `${intrinsicHeight.getValue() || window.innerHeight}px` : "100%"}; width: ${targetParent === document.body ? "100vw" : "100%"}; display: flex; justify-content: center; z-index: 1003;`
    );
    if (draggable) {
      wrapperElem.style.setProperty("overflow", "hidden");
    } else {
      wrapperElem.style.setProperty("align-items", "center");
    }
    return wrapperElem;
  }
  getInnerWrapper({ draggable, steps, startStep, maxHeightFactor }) {
    const wrapperElem = document.createElement("div");
    wrapperElem.classList.add("inner-wrapper");
    wrapperElem.setAttribute(
      "style",
      draggable ? `position: relative; max-height: calc(${intrinsicHeight.getValue() || window.innerHeight}px - env(safe-area-inset-top)); height: ${(intrinsicHeight.getValue() || window.innerHeight) * ((steps ? steps[typeof startStep === "number" ? startStep : steps.length - 1].height : 75) / 100)}px; width: 100%; transform: translateY(100%); opacity: 0; transition: opacity .3s ease, transform .3s ease;` : `max-height: ${maxHeightFactor * (intrinsicHeight.getValue() || window.innerHeight)}px); transform: scale(.8); opacity: 0; transition: opacity .3s ease, transform .3s ease;`
    );
    return wrapperElem;
  }
  getContentWrapper(options) {
    const contentWrapper = document.createElement("div");
    contentWrapper.classList.add("content-wrapper");
    contentWrapper.setAttribute(
      "style",
      "overflow: auto; overscroll-behavior: contain; height: " + (options.draggable ? `calc(100% - 2.2em); margin-top: 2.2em;` : `100%; max-height: ${options.maxHeightFactor * (intrinsicHeight.getValue() || window.innerHeight)}px;`)
    );
    return contentWrapper;
  }
  getDragWrapper(options) {
    const dragWrapper = document.createElement("div");
    dragWrapper.classList.add("drag-wrapper");
    dragWrapper.setAttribute(
      "style",
      "position: absolute; top: 0; left: 0; right: 0; display: flex; justify-content: center; padding: 1em; transform: translate3d(0,0,0);"
    );
    return dragWrapper;
  }
  getDragElement(options) {
    const dragElement = document.createElement("div");
    dragElement.classList.add("drag-element");
    dragElement.setAttribute(
      "style",
      "height: .2em; width: 4em; background-color: gray; opacity: 0.4; border-radius: 10%;"
    );
    return dragElement;
  }
  open() {
    if (!this.dialogElements || !this.options)
      throw new Error(`operation on uninitialized dialog! ${this.nodeName}`);
    const { targetParent = document.body } = this.options;
    this.dialogElements.outerWrapper.appendChild(this.dialogElements.backdrop);
    this.dialogElements.outerWrapper.appendChild(this.dialogElements.innerWrapper);
    this.dialogElements.innerWrapper.appendChild(this.dialogElements.contentWrapper);
    this.dialogElements.contentWrapper.appendChild(this);
    if (this.options.draggable && this.dialogElements.dragWrapper && this.dialogElements.dragElement) {
      this.dialogElements.dragWrapper.appendChild(this.dialogElements.dragElement);
      this.dialogElements.innerWrapper.appendChild(this.dialogElements.dragWrapper);
    }
    targetParent.appendChild(this.dialogElements.outerWrapper);
    this.result = this.result.finally(() => {
      if (!this.dialogElements || !this.options)
        throw new Error(`operation on uninitialized dialog! ${this.nodeName}`);
      targetParent.removeChild(this.dialogElements.outerWrapper);
      this.dialogElements.outerWrapper.removeChild(this.dialogElements.innerWrapper);
      if (this.options.draggable && this.dialogElements.dragWrapper && this.dialogElements.dragElement) {
        this.dialogElements.dragWrapper.removeChild(this.dialogElements.dragElement);
        this.dialogElements.innerWrapper.removeChild(this.dialogElements.dragWrapper);
      }
      this.dialogElements.contentWrapper.removeChild(this);
      this.dialogElements.innerWrapper.removeChild(this.dialogElements.contentWrapper);
      this.dialogElements.outerWrapper.removeChild(this.dialogElements.backdrop);
    });
    return this;
  }
  static create(options) {
    const dialog = document.createElement(this.element_name);
    dialog.initDialog(options);
    return dialog;
  }
  static showPopup(options = {}) {
    const dialog = this.create(options);
    if (options.content) {
      const closeAndResolve = (value) => dialog.close(value);
      const closeAndReject = (value) => dialog.cancel(value);
      const contentToUse = typeof options.content === "function" ? options.content(closeAndResolve, closeAndReject, dialog) : options.content;
      dialog.dialogShowPopupContent = contentToUse;
    }
    return dialog.open().result;
  }
};
ElementsPlainDialog = __decorateClass([
  customElement("x-plain-dialog")
], ElementsPlainDialog);
function line(x1, y1, x2, y2) {
  const m = (y2 - y1) / (x2 - x1);
  const b = y2 - m * x2;
  return (x) => x * m + b;
}
function stepFade(steps, backOpacity) {
  const funcs = steps.map((step, i) => {
    const lastStep = steps[i - 1];
    if (!lastStep)
      return;
    if (lastStep.backgroundInteraction === step.backgroundInteraction) {
      const val = step.backgroundInteraction ? 0 : backOpacity;
      return (x) => val;
    }
    return line(
      lastStep.height,
      lastStep.backgroundInteraction ? 0 : backOpacity,
      step.height,
      step.backgroundInteraction ? 0 : backOpacity
    );
  });
  funcs[0] = (x) => 0;
  funcs[funcs.length] = (x) => backOpacity;
  return (x) => {
    let i = 0;
    while (x > steps[i].height)
      i++;
    return Math.max(0, Math.min(backOpacity, funcs[i](x)));
  };
}
var DialogDragHandler = class {
  constructor(dialog) {
    this.dialog = dialog;
    this.maxV = 0;
    this.minV = 0;
    this.yPosition = 100;
    this.grabbed = false;
    this.grabOffset = 0;
    if (!dialog.dialogElements || !dialog.dialogElements.dragWrapper || !dialog.options)
      throw new Error(`connecting uninitialized dialog! ${dialog.nodeName}`);
    this.options = { ...defaultOptions, ...dialog.options };
    const stopListening = dialog.disconnected.pipe(skip(1));
    this.westuresRegion = new Region();
    this.westuresRegion.addGesture(new Pan(dialog.dialogElements.dragWrapper, (data) => this._update.next(data)));
    this.westuresRegion.addGesture(
      new Track(dialog.dialogElements.dragWrapper, (data) => this.trackPhase(data), {
        phases: ["start", "end"]
      })
    );
    this.currentStep = this.options.startStep;
    this.tallestHeight = this.options.steps[this.options.steps.length - 1].height;
    this.shortestHeight = this.options.steps[0].height;
    this.backgroundFadeFunction = stepFade(this.options.steps, this.options.backgroundOpacity);
    if (!this.options.steps[this.currentStep].dialogInteraction)
      dialog.dialogElements.dragWrapper.style.bottom = "0px";
    intrinsicHeight.pipe(skip(1), takeUntil(stopListening)).subscribe((height) => {
      if (!dialog.dialogElements)
        return;
      dialog.dialogElements.innerWrapper.style.height = this.getContentHeight(this.currentStep) + "px";
      dialog.dialogElements.innerWrapper.style.transform = this.options.steps[this.currentStep].height >= 95 ? `translateY(calc(${this.getTranslateDistance(this.currentStep)}px + env(safe-area-inset-top)))` : `translateY(${this.getTranslateDistance(this.currentStep)}px)`;
    });
    this._update = new Subject();
    this._update.pipe(throttleTime(0, animationFrameScheduler), takeUntil(stopListening)).subscribe((data) => this.handlePanMove(data));
    this.state = new BehaviorSubject({
      step: this.options.startStep,
      distanceFromStep: 0
    });
  }
  get elements() {
    if (!this.dialog.dialogElements)
      throw new Error(`operation on uninitialized dialog! ${this.dialog.nodeName}`);
    if (!this.dialog.dialogElements.dragElement || !this.dialog.dialogElements.dragWrapper || !this.dialog.dialogElements.contentWrapper)
      throw new Error(`Drag handler on dialog without drag elements. ${this.dialog.nodeName}`);
    return this.dialog.dialogElements;
  }
  getTranslateDistance(step) {
    if (step > this.options.steps.length - 1)
      step = this.options.steps.length - 1;
    if (step < 0)
      step = 0;
    return (intrinsicHeight.getValue() || window.innerHeight) * ((100 - this.options.steps[step].height) / 100);
  }
  getContentHeight(step) {
    if (step > this.options.steps.length - 1)
      step = this.options.steps.length - 1;
    if (step < 0)
      step = 0;
    return (intrinsicHeight.getValue() || window.innerHeight) * (this.options.steps[step].height / 100);
  }
  getGrabOffset(centerY) {
    return centerY - this.getTranslateDistance(this.currentStep);
  }
  getCandidateSteps() {
    let i = 0;
    while (this.options.steps[i].height < this.yPosition)
      i++;
    if (i === this.options.steps.length)
      i = this.options.steps.length - 1;
    return [this.options.steps[i - 1], this.options.steps[i] || this.options.steps[i - 1]];
  }
  trackPhase(data) {
    data.phase === "end" ? this.handlePanEnd(data) : this.handlePanStart(data);
  }
  async handlePanStart(e) {
    const { innerWrapper, contentWrapper, outerWrapper, backdrop, dragElement, dragWrapper } = this.elements;
    this.grabbed = true;
    this.maxV = 0;
    this.minV = 0;
    this.grabOffset = this.getGrabOffset(e.centroid.y);
    const newPosition = Math.max(0, e.centroid.y - this.grabOffset);
    dragWrapper.style.removeProperty("bottom");
    contentWrapper.scrollTop = 0;
    contentWrapper.style.pointerEvents = "none";
    innerWrapper.style.transition = "none";
    innerWrapper.style.height = this.getContentHeight(this.options.steps.length - 1) + "px";
    outerWrapper.style.transition = "none";
    backdrop.style.transition = "none";
    await animationFrame();
    outerWrapper.style.transform = "translateY(0)";
    innerWrapper.style.transform = `translateY(${newPosition}px)`;
    this.yPosition = 100 - newPosition / (intrinsicHeight.getValue() || window.innerHeight) * 100;
    dragElement.style.opacity = "1";
    this.state.next({ step: this.currentStep, distanceFromStep: 0 });
  }
  async handlePanMove(e) {
    const { innerWrapper, backdrop } = this.elements;
    if (!this.grabbed)
      return;
    this.maxV = Math.max(this.maxV, e.translation.y);
    this.minV = Math.min(this.minV, e.translation.y);
    const viewHeight = intrinsicHeight.getValue() || window.innerHeight;
    const newPosition = Math.min(
      (1 - this.shortestHeight / 100) * viewHeight - 1,
      Math.max((1 - this.tallestHeight / 100) * viewHeight, e.centroid.y - this.grabOffset)
    );
    this.yPosition = 100 - newPosition / viewHeight * 100;
    backdrop.style.opacity = this.backgroundFadeFunction(this.yPosition) + "";
    innerWrapper.style.transform = `translateY(calc(${newPosition}px))`;
    this.state.next({
      step: this.currentStep,
      distanceFromStep: this.options.steps[this.currentStep].height - this.yPosition
    });
  }
  async handlePanEnd(e) {
    const { innerWrapper, contentWrapper, outerWrapper, backdrop, dragElement, dragWrapper } = this.elements;
    if (!this.grabbed)
      return;
    this.grabbed = false;
    this.grabOffset = 0;
    dragElement.style.opacity = "0.4";
    const candidateSteps = this.getCandidateSteps();
    const nextStep = this.minV !== 0 && this.maxV !== 0 ? candidateSteps[this.yPosition - candidateSteps[0].height > candidateSteps[1].height - this.yPosition ? 1 : 0] : this.minV && !this.maxV ? candidateSteps[1] : this.maxV && !this.minV ? candidateSteps[0] : this.options.steps[this.currentStep];
    const nextStepIndex = this.options.steps.findIndex((step) => step === nextStep);
    if (nextStepIndex === -1)
      throw new Error("could not find nextStep!");
    if (nextStepIndex === 0 && this.options.closeOnBottomStep)
      this.dialog.close(void 0);
    outerWrapper.style.transition = "transform .3s";
    innerWrapper.style.transition = "transform .3s, opacity .3s";
    backdrop.style.transition = "opacity .3s ease";
    await animationFrame();
    if (!nextStep.dialogInteraction)
      dragWrapper.style.bottom = "0px";
    contentWrapper.style.pointerEvents = "unset";
    outerWrapper.style.transform = "translateY(0)";
    innerWrapper.style.transform = nextStep.height >= 95 ? `translateY(calc(${this.getTranslateDistance(nextStepIndex)}px + env(safe-area-inset-top)))` : `translateY(${this.getTranslateDistance(nextStepIndex)}px)`;
    if (nextStep.backgroundInteraction) {
      backdrop.style.opacity = "0";
      if (nextStepIndex !== 0 || !this.options.closeOnBottomStep)
        firstValueFrom(timer(300)).then(() => {
          innerWrapper.style.transition = "none";
          innerWrapper.style.transform = nextStep.height >= 95 ? "translateY(env(safe-area-inset-top))" : `translateY(0)`;
          innerWrapper.style.height = this.getContentHeight(nextStepIndex) + "px";
          outerWrapper.style.transition = "none";
          outerWrapper.style.transform = `translateY(${this.getTranslateDistance(nextStepIndex)}px)`;
        });
    } else {
      backdrop.style.opacity = this.options.backgroundOpacity + "";
    }
    this.currentStep = nextStepIndex;
    this.state.next({ step: this.currentStep, distanceFromStep: 0 });
  }
};
export {
  ElementsPlainDialog,
  OpenState
};
